import React, { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "utilities/i18n";
import {
  CheckboxProps,
  Input,
  InputProps,
  Select,
  SelectProps,
  ToggleSwitch,
  useField,
  useFormikContext
} from "collections/Form";
import { RangeInput, RangeInputProps } from "collections/Form/RangeInput";
import { Loader, ToolTip } from "elements";
import { NavLink } from "polyrouter";
import { useSelector } from "react-redux";
import { State } from "store";
import { SettingChange } from "store/desktopDeviceSettings";
import { DesktopDeviceFormSetting } from "../Settings-Desktop/DesktopDeviceSettings";
import {
  DesktopDevicePolicyScope,
  useDesktopDevicePolicyScopeContext
} from "../Settings-Desktop/ProvisionedDesktopDeviceSettingsRouter";
import {
  BooleanSettingNotEmptyProps,
  SettingEmptyProps,
  StringSettingNotEmptyProps
} from "../Settings-Desktop/schemas/types";
import { SettingChangeIndicator } from "../Settings/ProvisionedDeviceSettingsRouter";
import { InheritanceDetails } from "store/desktopDeviceSettingInheritance";
import { Permissions } from "../../../Admin/Profile/PermissionsCard";
import { SettingIcon } from "../Settings/ProvisionedDeviceSettings";
import { Link } from "polyrouter";
import { PolicyDeploymentScope } from "generated/graphql";

export interface SettingSpec {
  url: string;
  title: string;
}

type PolicyLevelKeys =
  | "POLICY_LEVEL_DEVICE"
  | "POLICY_LEVEL_DEVICE_MODEL"
  | "POLICY_LEVEL_GROUP"
  | "POLICY_LEVEL_MODEL"
  | "POLICY_LEVEL_SITE"
  | "POLICY_LEVEL_USER_GROUP";
export const useInheritanceLabel = (
  changed?: string,
  inheritance?: InheritanceDetails,
  state?: DesktopDevicePolicyScope | PolicyDeploymentScope,
  hardwareModel?: string
) => {
  const { t } = useTranslation();
  if (changed === SettingChange.REVERTED) {
    return t("REVERTED_BY_USER");
  }

  if (state !== "DEVICE") {
    return null;
  }

  if (changed === SettingChange.EDITED && state) {
    const levelKey: PolicyLevelKeys = `POLICY_LEVEL_${state}` as const;
    return t(levelKey);
  }
  if (inheritance) {
    let pathname = "/manage/policy";
    let linkSearch = "";
    const inheritanceScope = inheritance?.scope.toLocaleLowerCase();
    const inheritanceId = inheritance?.id;
    if (inheritanceScope && inheritanceId && hardwareModel) {
      if (
        inheritance.scope === DesktopDevicePolicyScope.Model ||
        inheritance.scope === PolicyDeploymentScope.DeviceModel
      ) {
        linkSearch = `deviceModel=${hardwareModel}&previous=device`;
      } else {
        linkSearch = `deviceModel=${hardwareModel}&${inheritanceScope}=${inheritanceId}&previous=device`;
      }
    }

    if (linkSearch) {
      pathname = "/manage/policy/details/settings";
    }

    switch (inheritance.scope) {
      case DesktopDevicePolicyScope.Device || PolicyDeploymentScope.Device:
        return t("POLICY_LEVEL_DEVICE");
      case PolicyDeploymentScope.ExternalDevice:
        return t("EXTERNAL_DEVICE_POLICY");
      case PolicyDeploymentScope.Global:
        return t("GLOBAL_POLICY");
      default:
        return (
          <>
            {t(inheritance?.scope) + ": "}
            <Link
              to={{
                pathname: pathname,
                search: linkSearch
              }}
            >
              {inheritance?.value}
            </Link>
          </>
        );
    }
  }
};

export const DEVICE_SETTINGS: Record<string, SettingSpec> = {
  DISPLAY: {
    url: "/display",
    title: i18next.t("DISPLAY")
  },
  DATE: {
    url: "/date",
    title: i18next.t("DATE_AND_TIME")
  },
  CALENDAR: {
    url: "/calendar",
    title: i18next.t("CALENDAR")
  },
  SERVERS: {
    url: "/servers",
    title: i18next.t("CALL_SERVER")
  },
  NETWORK: {
    url: "/network",
    title: i18next.t("NETWORK_QUALITY")
  },
  ADVANCED: {
    url: "/advanced",
    title: i18next.t("SETTINGS_ADVANCED")
  }
};

export const DESKTOP_DEVICE_SETTINGS_NAV: Record<string, SettingSpec> = {
  LANGUAGE: {
    url: "/language",
    title: i18next.t("LANGUAGE")
  },
  GENERAL: {
    url: "/general",
    title: i18next.t("GENERAL")
  },
  RINGTONES_AND_VOLUME: {
    url: "/ringtones-volume",
    title: i18next.t("RINGTONES_AND_VOLUME")
  },
  AUDIO: {
    url: "/audio",
    title: i18next.t("AUDIO")
  },
  SENSORS_AND_PRESENCE: {
    url: "/sensors-presence",
    title: i18next.t("SENSORS_AND_PRESENCE")
  },
  SOFTPHONES_AND_MEDIA_PLAYERS: {
    url: "/softphones-media-players",
    title: i18next.t("SOFTPHONES_AND_MEDIA_PLAYERS")
  },
  WIRELESS: {
    url: "/wireless",
    title: i18next.t("WIRELESS")
  },
  ADVANCED: {
    url: "/advanced",
    title: i18next.t("SETTINGS_ADVANCED")
  },
  VIDEO_CONTROLS: {
    url: "/video-controls",
    title: i18next.t("VIDEO_CONTROLS")
  },
  DIAGNOSTICS: {
    url: "/diagnostics",
    title: i18next.t("DIAGNOSTICS")
  },
  RESET_DEVICE: {
    url: "/reset-device",
    title: i18next.t("RESET_DEVICE")
  },
  SOFTWARE_UPDATE: {
    url: "/software-update",
    title: i18next.t("SOFTWARE_UPDATE")
  }
};

export const InactiveInputToolTip: React.FC<{
  permission: string;
}> = ({ permission }) => {
  const { t } = useTranslation();
  return (
    <ToolTip iconTitle="help" className="debug-tooltip" position="right">
      <div data-testid="inactive-tooltip-message">
        {t("NEEDS_PERMISSION", { required: permission })}
      </div>
    </ToolTip>
  );
};

export const SettingsCategory: React.FC<{ title: string }> = ({
  title,
  children
}) => (
  <div className="settings">
    <div className="card title-card">{title}</div>
    {children}
  </div>
);

export const FormSelect: React.FC<Omit<SelectProps, "placeholder"> &
  Pick<DesktopDeviceFormSetting, "revert"> & {
    label: string;
    disabled?: boolean;
    permission?: string;
    inheritance?: InheritanceDetails;
    tooltipText?: string;
    changed?: string;
  }> = ({
  name,
  label,
  options,
  disabled,
  permission,
  tooltipText,
  inheritance,
  changed,
  revert,
  ...rest
}) => {
  const [, { value, touched, error }] = useField(name);
  const { t } = useTranslation();
  const debugUserRoles = useSelector((state: State) => {
    return state.env.debugUserRoles;
  });
  const [state] = useDesktopDevicePolicyScopeContext();

  const hardwareModel = useSelector(
    (state: State) => state.desktopDeviceSettingInheritance["hardwareModel"]
  );
  let inheritanceLabel = useInheritanceLabel(
    changed,
    inheritance,
    state.desktopDevicePolicyScope,
    hardwareModel?.value
  );
  if (value === undefined) {
    return null;
  }

  const classes = [
    "form-group",
    touched ? "touched" : "",
    error ? "has-error" : ""
  ]
    .filter(c => !!c)
    .join(" ");

  return (
    <div className={`${classes}`}>
      <label className="col-4 text-right">
        {changed && <SettingChangeIndicator />}
        {tooltipText && (
          <ToolTip icon="help_circle" iconTitle="help" position="bottom">
            {tooltipText}
          </ToolTip>
        )}
        {t(label)}:
      </label>
      <div className="col-4">
        <Select
          placeholder={options[0]?.text || ""}
          name={name}
          options={options}
          {...rest}
          disabled={disabled}
        />
      </div>
      <div className="col-2 text-md" data-testid="inheritance">
        {inheritanceLabel && inheritanceLabel}
      </div>
      <div className="col-2">
        <SettingIcon settingName={name} revert={revert} />
      </div>
      {debugUserRoles && (
        <InactiveInputToolTip
          permission={permission ? permission : Permissions.UPDATE_DEVICE}
        />
      )}
    </div>
  );
};

export const FormInput: React.FC<InputProps & {
  disabled?: boolean;
  permission?: string;
}> = ({ name, label, disabled, permission, ...rest }) => {
  const { t } = useTranslation();
  const debugUserRoles = useSelector((state: State) => {
    return state.env.debugUserRoles;
  });

  const id = rest.id || `${name}-input`;
  return (
    <div className="form-group">
      <label className="col-4 text-right" htmlFor={id}>
        {label && t(label)}:
      </label>
      <div className="col-4">
        <Input {...rest} name={name} disabled={disabled} />
      </div>
      {debugUserRoles && (
        <InactiveInputToolTip
          permission={permission ? permission : Permissions.UPDATE_DEVICE}
        />
      )}
    </div>
  );
};

export const FormCheckbox: React.FC<CheckboxProps &
  Pick<DesktopDeviceFormSetting, "revert"> & {
    inheritance?: InheritanceDetails;
    tooltipText?: string;
    changed?: string;
    disabled?: boolean;
  }> = ({
  name,
  tooltipText,
  disabled,
  changed,
  revert,
  inheritance,
  ...rest
}) => {
  const { t } = useTranslation();
  const [{ value }] = useField(name);
  const debugUserRoles = useSelector((state: State) => {
    return state.env.debugUserRoles;
  });
  const [state] = useDesktopDevicePolicyScopeContext();
  const hardwareModel = useSelector(
    (state: State) => state.desktopDeviceSettingInheritance["hardwareModel"]
  );
  let inheritanceLabel = useInheritanceLabel(
    changed,
    inheritance,
    state.desktopDevicePolicyScope,
    hardwareModel?.value
  );

  if (value === undefined) {
    return null;
  }

  return (
    <div className="form-group">
      <label className="col-4 text-right" htmlFor={name}>
        {changed && <SettingChangeIndicator />}
        {tooltipText && (
          <ToolTip icon="help_circle" iconTitle="help" position="bottom">
            {tooltipText}
          </ToolTip>
        )}
        {rest.label && t(rest.label)}:
      </label>
      <div className="col-4">
        <ToggleSwitch name={name} disabled={disabled} {...rest} />
      </div>
      <div className="col-2 text-md" data-testid="inheritance">
        {inheritanceLabel && inheritanceLabel}
      </div>
      <div className="col-2">
        <SettingIcon settingName={name} revert={revert} />
      </div>
      {debugUserRoles && (
        <InactiveInputToolTip permission={Permissions.UPDATE_DEVICE} />
      )}
    </div>
  );
};

export const ErrorDisplay: React.FC = () => {
  const { dirty, errors } = useFormikContext();
  const { t } = useTranslation();

  let messages =
    dirty && Object.keys(errors).length > 0 ? (
      <div className="alert alert-danger col-12">
        {t("DEVICE_SETTINGS_CORRECT_ERRORS")}
      </div>
    ) : null;

  return messages ? (
    <div className="alert-container grid text-right pb-1 mb-75 block">
      {messages}
    </div>
  ) : (
    <></>
  );
};

export const FormSlider: React.FC<RangeInputProps &
  Pick<DesktopDeviceFormSetting, "revert"> & {
    values:
      | SettingEmptyProps
      | StringSettingNotEmptyProps
      | BooleanSettingNotEmptyProps;
    changed?: string;
    disabled?: boolean;
    tooltipText?: string;
    inheritance?: InheritanceDetails;
  }> = ({
  name,
  tooltipText,
  children,
  values,
  disabled,
  changed,
  inheritance,
  revert,
  ...rest
}) => {
  const { t } = useTranslation();

  const debugUserRoles = useSelector((state: State) => {
    return state.env.debugUserRoles;
  });
  const [state] = useDesktopDevicePolicyScopeContext();

  const hardwareModel = useSelector(
    (state: State) => state.desktopDeviceSettingInheritance["hardwareModel"]
  );
  let inheritanceLabel = useInheritanceLabel(
    changed,
    inheritance,
    state.desktopDevicePolicyScope,
    hardwareModel?.value
  );

  if (!values || values.value === undefined) return null;

  const [min, max, step] = values.constraints.options;

  // convert slider to checkbox due to constraint range
  if (min === "0" && max === "1" && step === "1") {
    return (
      <FormCheckbox
        changed={changed}
        name={name}
        tooltipText={tooltipText}
        disabled={disabled}
        revert={revert}
        {...rest}
      />
    );
  }

  return (
    <div className="form-group">
      <label className="col-4 text-right" htmlFor={name}>
        {changed && <SettingChangeIndicator />}
        {tooltipText && (
          <ToolTip icon="help_circle" iconTitle="help" position="bottom">
            {tooltipText}
          </ToolTip>
        )}
        {rest.label && t(rest.label)}:
      </label>
      <div className="col-4">
        <RangeInput
          name={name}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
        />
        {children}
      </div>
      <div className="col-2 text-md" data-testid="inheritance">
        {inheritanceLabel && inheritanceLabel}
      </div>
      <div className="col-2">
        <SettingIcon settingName={name} revert={revert} />
      </div>
      {debugUserRoles && (
        <InactiveInputToolTip permission={Permissions.UPDATE_DEVICE} />
      )}
    </div>
  );
};

export const FormSliderAutoBtn: React.FC<{
  name: string;
}> = ({ name, ...rest }) => {
  const { t } = useTranslation();
  const [, { value }, { setValue }] = useField(name);
  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    setValue(!value);
  };

  return (
    <button
      className={`btn btn-link slider-link ${value ? "active" : "inactive"}`}
      onClick={handleClick}
      {...rest}
    >
      {t("AUTO")}
    </button>
  );
};

export const SettingsNav: React.FC<{
  showAdvanced: boolean;
  settings: Record<string, SettingSpec>;
}> = ({ showAdvanced, settings }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<undefined | string>(undefined);
  const clearFilter = () => setFilter(undefined);
  const filterVal = (val: string) =>
    val.toLowerCase().includes((filter ?? "").toLowerCase());

  return (
    <ul className="menu router sub">
      <li className="filter">
        <input
          data-testid="settings-filter"
          type="text"
          id="filter-input"
          className="form-control floating-input"
          placeholder={t("FILTER_SETTINGS")}
          value={filter ?? ""}
          onFocus={clearFilter}
          onChange={e => setFilter(e.target.value)}
        />
      </li>
      {Object.entries(settings)
        .filter(([category]) => category !== "ADVANCED" || showAdvanced)
        .map(([, spec]) => spec)
        .filter(({ title }) => filterVal(title))
        .map(({ title, url }) => (
          <li key={title + "key"}>
            <NavLink
              persist={["DeviceFilterState", "InsightsFilterState"]}
              nest
              exact
              to={url}
            >
              {title}
            </NavLink>
          </li>
        ))}
    </ul>
  );
};

export const SettingsNavDesktop = () => {
  const { t } = useTranslation();
  const { navItems, loading } = useSelector((state: State) => {
    return state.settingsNavItems;
  });

  const [filter, setFilter] = useState<undefined | string>(undefined);
  const clearFilter = () => setFilter(undefined);
  const filterVal = (val: string) =>
    val.toLowerCase().includes((filter ?? "").toLowerCase());

  return (
    <Loader loading={loading} className="inline text-left">
      <ul className="menu router sub">
        <li className="filter">
          <input
            data-testid="settings-filter"
            type="text"
            id="filter-input"
            className="form-control floating-input"
            placeholder={t("FILTER_SETTINGS")}
            value={filter ?? ""}
            onFocus={clearFilter}
            onChange={e => setFilter(e.target.value)}
          />
        </li>

        {navItems
          .filter(({ title }) => filterVal(title))
          .map(({ title, url }) => (
            <li key={title + "key"}>
              <NavLink
                persist={["DeviceFilterState", "InsightsFilterState"]}
                nest
                exact
                to={url}
              >
                {title}
              </NavLink>
            </li>
          ))}
      </ul>
    </Loader>
  );
};
